import { useEffect } from "react";

import { useLocation } from "react-router-dom";

import { useReplaceSearchParams } from "../../hooks";

import {
    DataParamsApi,
    DataParamsCodecMap,
    deleteDataParams,
    encodeDataParam,
    resolveDataParams,
} from "./dataParams";
import { useTableStateStorage } from "./useTableStateStorage";

/**
 * Data-source params of a table, read from the URL. localStorage mirrors
 * whatever the URL resolves to, under the table's `dataParams`, so a later
 * `restore` brings back exactly what was on screen.
 */
export function useDataParams<M extends DataParamsCodecMap>(
    tableId: string,
    codecMap: M,
    dataDefaults: Record<string, unknown>
): DataParamsApi<M> {
    const { search } = useLocation();
    const [, , , setStoredDataParams] = useTableStateStorage(tableId);
    const replaceSearchParams = useReplaceSearchParams();

    const values = resolveDataParams(
        codecMap,
        new URLSearchParams(search),
        dataDefaults
    );

    const hasDataParams = Object.keys(codecMap).length > 0;
    const serializedValues = JSON.stringify(values);
    useEffect(() => {
        if (!hasDataParams) {
            return;
        }
        const storedValues: Record<string, unknown> =
            JSON.parse(serializedValues);
        setStoredDataParams(storedValues);
    }, [hasDataParams, serializedValues, setStoredDataParams]);

    return {
        values,
        setValue: (key, value) => {
            const paramKey = key as string;
            replaceSearchParams((params) => {
                const encoded = encodeDataParam(
                    codecMap[paramKey],
                    value,
                    dataDefaults[paramKey]
                );
                if (encoded === null) {
                    params.delete(paramKey);
                } else {
                    params.set(paramKey, encoded);
                }
            });
        },
        reset: () => {
            replaceSearchParams((params) => deleteDataParams(params, codecMap));
        },
    };
}
